/**
 * The audit trail: who did what in the organization, as the cloud keeps it
 * (vms_database/internal/model/audit.go). One shape serves the read API and,
 * uploaded by a media_server, the wire.
 */

import { z } from 'zod';

import { checkDeviceIdOf, DeviceIdSchema } from './deviceId';
import {
    AuditDetails as GeneratedAuditDetailsSchema,
    AuditRecord as GeneratedAuditRecordSchema,
    AuditRef as AuditRefSchema,
} from './generated/vms_database.zod';
import type { PanelId, SelectCamera } from './panels';
import type { LoadState } from './snapshot';

/** A record type: the kind of object acted on, or an event about no object. */
export const AuditType = {
    Login: 'login',
    UnauthorizedLogin: 'unauthorizedLogin',
    View: 'view',
    Export: 'export',
    Device: 'device',
    DeviceReplacement: 'deviceReplacement',
    Server: 'server',
    ServerReplacement: 'serverReplacement',
    Storage: 'storage',
    User: 'user',
    Rule: 'rule',
    Bookmark: 'bookmark',
    Case: 'case',
    Settings: 'settings',
    Update: 'update',
    Chat: 'chat',
    IoOutputPress: 'ioOutputPress',
    IoOutputHold: 'ioOutputHold',
    IoPortConfig: 'ioPortConfig',
} as const;
export type AuditType = (typeof AuditType)[keyof typeof AuditType];

/** What was done to the object, for the types that are about one. */
export const AuditAction = {
    Insert: 'insert',
    Update: 'update',
    Remove: 'remove',
} as const;
export type AuditAction = (typeof AuditAction)[keyof typeof AuditAction];

export const AuditTypeSchema = z.enum(AuditType);

/** The record types whose `resourceId` is a device id: a camera's or an I/O module's. */
const DEVICE_RESOURCE_TYPES: ReadonlySet<AuditType> = new Set([
    AuditType.Device,
    AuditType.DeviceReplacement,
    AuditType.IoOutputPress,
    AuditType.IoOutputHold,
    AuditType.IoPortConfig,
]);
export const AuditActionSchema = z.enum(AuditAction);

/** An object a record refers to: the id links to it while it exists, the name outlives it. */
export { AuditRefSchema };
export type AuditRef = z.infer<typeof AuditRefSchema>;

/** A camera a record refers to. */
export const AuditDeviceRefSchema = AuditRefSchema.extend({
    id: DeviceIdSchema,
});

/** What a reader needs beyond the record's columns; only the parts the record has. */
export const AuditDetailsSchema = GeneratedAuditDetailsSchema.extend({
    devices: z.array(AuditDeviceRefSchema).optional(),
});
export type AuditDetails = z.infer<typeof AuditDetailsSchema>;

export const AuditRecordSchema = GeneratedAuditRecordSchema.extend({
    details: AuditDetailsSchema,
}).check(checkDeviceIdOf('resourceId', (record) => record.resourceId !== undefined &&
    DEVICE_RESOURCE_TYPES.has(record.type)));
export type AuditRecord = z.infer<typeof AuditRecordSchema>;

/** The audit log panel's two views. */
export const AuditView = {
    Records: 'records',
    Sessions: 'sessions',
} as const;
export type AuditView = (typeof AuditView)[keyof typeof AuditView];

/** What clicking an audit row does, for the tooltip and the handler. */
export interface AuditRowAction {
    title: string;
    run: () => void;
}

/** What the row actions reach for: the objects that still exist, and the shell. */
export interface AuditRowActionContext {
    /** The listed cameras by id. */
    cameraById: ReadonlyMap<string, unknown>;
    servers: { id: string }[];
    users: { email: string }[];
    rules: { id: string }[];
    cases: { id: string }[];
    openPanel: (id: PanelId, props?: Record<string, unknown>) => void;
    selectCamera: SelectCamera;
    /** Set while the sessions list is showing: a sign-in opens its session. */
    openSession?: (record: AuditRecord) => void;
}

/** What `GET /audit` narrows by. A page ends before `beforeSeq`, newest first. */
export interface AuditListFilter {
    beforeSeq?: number;
    limit?: number;
    fromMs?: number;
    toMs?: number;
    actor?: string;
    sessionId?: string;
    types?: string[];
    deviceIds?: string[];
    /** One object's id: the records about it, a camera's among them by their cameras. */
    resourceId?: string;
}

/** What one feed of the audit log narrows by. The family adds the page cursor and the limit. */
export type AuditLogRequest = Omit<AuditListFilter, 'beforeSeq' | 'limit'>;

/** One feed of the audit log: the newest page and the older pages loaded below it. */
export interface AuditLogSnapshot extends LoadState {
    records: AuditRecord[];
    /** The last page came back full, so older records may follow. */
    hasOlder: boolean;
}
